import { ArrowUpCircle, ArrowDownCircle, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { Transaction } from '@/types/account-drilldown';
import { cn } from '@/lib/utils';

interface TransactionListProps {
  transactions: Transaction[];
  isLoading: boolean;
  onTransactionClick: (transaction: Transaction) => void;
  hasMore?: boolean;
  onLoadMore?: () => void;
}

export function TransactionList({
  transactions,
  isLoading,
  onTransactionClick,
  hasMore = false,
  onLoadMore,
}: TransactionListProps) {
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
                <Skeleton className="h-6 w-20" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <div className="flex flex-col items-center gap-2">
            <div className="rounded-full bg-muted p-3">
              <ArrowUpCircle className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="font-semibold text-lg">No transactions found</h3>
            <p className="text-sm text-muted-foreground max-w-sm">
              No transactions match your current filters. Try adjusting your date range or category selection.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" role="list" aria-label="Transaction list">
      {transactions.map((transaction) => {
        const isIncome = transaction.type === 'income';
        const Icon = isIncome ? ArrowUpCircle : ArrowDownCircle;
        const iconColor = isIncome ? 'text-green-600' : 'text-red-600';
        const amountColor = isIncome ? 'text-green-600' : 'text-red-600';

        return (
          <Card
            key={transaction.id}
            className="cursor-pointer transition-colors hover:bg-accent focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
            role="article"
            aria-label={`${isIncome ? 'Income' : 'Expense'} transaction: ${transaction.description || 'Untitled transaction'}`}
          >
            <CardContent className="p-4">
              <button
                onClick={() => onTransactionClick(transaction)}
                className="w-full text-left focus:outline-none"
                aria-label={`View details for ${transaction.description || 'Untitled transaction'}, ${formatCurrency(transaction.amount, transaction.currency)} on ${formatDate(transaction.transaction_date)}`}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {/* Icon */}
                    <div className={cn('flex-shrink-0', iconColor)} aria-hidden="true">
                      <Icon className="h-10 w-10" />
                    </div>

                    {/* Transaction Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium truncate">
                          {(transaction.description && transaction.description.trim() !== '') 
                            ? transaction.description 
                            : 'Untitled transaction'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>{formatDate(transaction.transaction_date)}</span>
                        {transaction.categories && (
                          <>
                            <span aria-hidden="true">•</span>
                            <span className="truncate">
                              {transaction.categories.icon && <span aria-hidden="true">{transaction.categories.icon} </span>}
                              {transaction.categories.name}
                            </span>
                          </>
                        )}
                        {!transaction.categories && (
                          <>
                            <span aria-hidden="true">•</span>
                            <span className="text-muted-foreground/70">Uncategorized</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Amount and Arrow */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={cn('font-semibold text-lg', amountColor)}>
                      <span className="sr-only">{isIncome ? 'Income' : 'Expense'}: </span>
                      {isIncome ? '+' : '-'}
                      {formatCurrency(transaction.amount, transaction.currency)}
                    </span>
                    <ChevronRight className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                  </div>
                </div>
              </button>
            </CardContent>
          </Card>
        );
      })}

      {/* Load More Button */}
      {hasMore && onLoadMore && (
        <div className="flex justify-center pt-4">
          <Button variant="outline" onClick={onLoadMore}>
            Load more transactions
          </Button>
        </div>
      )}
    </div>
  );
}
